import { getAllDriver, getAllSupplier, getAllVehicleNumber, getUser } from "_services";
import { getCityLocationList } from "_services/accountsServices/cityLocationService";
import {
  getBranchListApi,
  getConsignorDropdownListData2,
} from "_services/dropdownServices/dropdownService";
import {
  CityDropdownForTrip,
  CommonDatePicker,
  CommonDateRange,
  CrossButton,
  CustomSelect,
  VehicleDropdownForTrip,
} from "components";
import { AddButton } from "components/common/CommonButton";
import { UserContext } from "context";
import moment from "moment";
import React, { useCallback, useContext, useEffect, useState, useMemo, useRef } from "react";
import toast from "react-hot-toast";
import StorageService from "utils/StorageServices";

const INITIAL_FILTER_STATE = {
  search: "",
  branch_id: "",
  branch_name: "",
  start_date: "",
  end_date: "",
  user_id: "",
  user_name: "",
  vehicle_id: "",
  vehicle_no: null, // ✅ Changed to null to store object
  expense_category_id: "",
  consignor_id: "",
  consignor_name: "",
  supplier_id: "",
  supplier_name: "",
  driver_id: "",
  driver_name: "",
  from_location: "",
  to_location: "",
  lr_status: 0,
  // pod_status: 0,
  trip_status: 0,
  plan_status: 0,
  responses: 0,
  vehicle_status: 0,
  stateCode: "",
};

const cityCache = new Map();
const pendingCityRequests = new Map();

const PodFollowupFilterModal = ({
  isOpen,
  onClose,
  filter,
  setFilter,
  isShow,
  list,
  name,
  filterCount
}) => {

  const { search = true } = isShow;
  const [modalFilter, setModalFilter] = useState(INITIAL_FILTER_STATE);
  const [fromLocationDetails, setFromLocationDetails] = useState({});
  const [toLocationDetails, setToLocationDetails] = useState({});
  const [vehicleNumber, setVehicleNumber] = useState({});
  const [branchDetails, setBranchDetails] = useState({});
  const [consignorDetails, setConsignorDetails] = useState({});
  const [supplierDetails, setSupplierDetails] = useState({});
  const [driverDetails, setDriverDetails] = useState({});
  const [userDetails, setUserDetails] = useState({});

  const companyIdRef = useRef(StorageService.getCompanyId());
  const firmIdRef = useRef(StorageService.getFirmId());

  const prefilledCheckDone = useRef(false);

  const {
    cities,
    masterData,
    tripFilter,
    setTRipFilter,
    states,
    setPlanFilter,
    planFilter,
    setSearchText,
    setVehicleDetails,
    vehicleDeatils
  } = useContext(UserContext);


  const dropdownLists = useMemo(() => {
    if (!masterData) return {};

    const createDropdownList = (data, hasAll = true) => {
      const formatted = data?.map(item => ({
        label: item.name,
        value: item.id,
      })) || [];
      return hasAll ? [{ label: "All", value: 0 }, ...formatted] : formatted;
    };

    return {
      ownershipList: createDropdownList(masterData.vehicle_ownership_list),
      tripStatusList: createDropdownList(masterData.trip_status_list),
      planStatusList: createDropdownList(masterData.plan_status_list, false),
      bidStatusList: createDropdownList(masterData.bid_status_list),
      responseList: createDropdownList(masterData.plan_responses_list),
      lrStatusList: createDropdownList(masterData?.lr_status_list),
      podStatusList: createDropdownList(masterData?.pod_status_list),
      vehicleStatusList: createDropdownList(masterData.vehicle_status_list),
      stateList: [
        { label: "All", value: 0 },
        ...(states?.map(state => ({
          label: state.name,
          value: state.isoCode,
        })) || [])
      ]
    };
  }, [masterData, states]);

  useEffect(() => {
    if (!isOpen) {
      prefilledCheckDone.current = false;
      return;
    }

    setModalFilter(prevFilter => ({ ...prevFilter, ...filter }));

    // ✅ Clear details if filter values are empty
    if (!filter.user_id) setUserDetails({});
    if (!filter.branch_id) setBranchDetails({});
    if (!filter.consignor_id) setConsignorDetails({});
    if (!filter.supplier_id) setSupplierDetails({});
    if (!filter.driver_id) setDriverDetails({});
    if (!filter.vehicle_id) setVehicleNumber({});

    // Prefill locations
    if (filter.from_location || filter.to_location) {
      getStateFromCity({
        fromcity: filter.from_location,
        tocity: filter.to_location
      });
    } else {
      setFromLocationDetails({});
      setToLocationDetails({});
    }

    // Prefill other dropdowns WITHOUT API call
    if (!prefilledCheckDone.current) {
      prefillDropdownDataWithoutAPI();
      prefilledCheckDone.current = true;
    }
  }, [isOpen, filter]);

  const getStateFromCity = useCallback(({ fromcity, tocity }) => {
    const findCityState = (cityName) => {
      const city = cities.find(c => c.name === cityName);
      if (!city) return null;

      const state = states.find(s => s.isoCode === city.stateCode);
      return state ? { name: cityName, label: `${cityName}, ${state.name}` } : null;
    };

    if (fromcity) {
      const details = findCityState(fromcity);
      if (details) setFromLocationDetails(details);
    }

    if (tocity) {
      const details = findCityState(tocity);
      if (details) setToLocationDetails(details);
    }
  }, [cities, states]);

  // ✅ Fixed Prefill function
  const prefillDropdownDataWithoutAPI = useCallback(() => {
    // Prefill Branch
    if (filter.branch_id && filter.branch_name) {
      setBranchDetails({
        _id: filter.branch_id,
        branch: filter.branch_name,
        label: filter.branch_name,
        value: filter.branch_id
      });
    }

    // Prefill Consignor
    if (filter.consignor_id && filter.consignor_name) {
      setConsignorDetails({
        _id: filter.consignor_id,
        name: filter.consignor_name,
        label: filter.consignor_name,
        value: filter.consignor_id
      });
    }

    // Prefill Supplier
    if (filter.supplier_id && filter.supplier_name) {
      setSupplierDetails({
        _id: filter.supplier_id,
        name: filter.supplier_name,
        label: filter.supplier_name,
        value: filter.supplier_id
      });
    }

    // Prefill Driver
    if (filter.driver_id && filter.driver_name) {
      setDriverDetails({
        _id: filter.driver_id,
        name: filter.driver_name,
        label: filter.driver_name,
        value: filter.driver_id
      });
    }

    // ✅ Fixed Vehicle Prefill - handles both object and string
    console.log('filter.vehicle_id && filter.vehicle_no', filter.vehicle_id, filter.vehicle_no)
    if (filter.vehicle_id && filter.vehicle_no) {
      const vehicleData = {
        _id: filter.vehicle_id,
        // Handle both object and string
        vehicle_no: typeof filter.vehicle_no === 'object'
          ? filter.vehicle_no.vehicle_no
          : filter.vehicle_no,
        vehicle_ownership: typeof filter.vehicle_no === 'object'
          ? filter.vehicle_no.vehicle_ownership
          : undefined,
        label: typeof filter.vehicle_no === 'object' && filter.vehicle_no.vehicle_ownership
          ? `${filter.vehicle_no.vehicle_no} | ${masterData?.vehicle_ownership[filter.vehicle_no.vehicle_ownership]}`
          : filter.vehicle_no,
        value: filter.vehicle_id
      };
      console.log('vehicleData', vehicleData)
      setVehicleNumber(vehicleData);
      setVehicleDetails(vehicleData);
    }

    // Prefill User
    if (filter.user_id && filter.user_name) {
      setUserDetails({
        _id: filter.user_id,
        name: filter.user_name,
        label: filter.user_name,
        value: filter.user_id
      });
    }
  }, [filter, setVehicleDetails, masterData]);

  const fetchFromCity = useCallback(async (search, page, limit) => {
    const cacheKey = `${search}-${page}-${limit}`;

    if (cityCache.has(cacheKey)) {
      return cityCache.get(cacheKey);
    }

    if (pendingCityRequests.has(cacheKey)) {
      return pendingCityRequests.get(cacheKey);
    }

    const requestPromise = (async () => {
      try {
        const response = await getCityLocationList({
          countryCode: "IN",
          stateCode: "",
          offset: page,
          limit,
          search,
        });

        if (response?.responseCode === 200) {
          const cities = response.data.all_cities.map(city => ({
            ...city,
            _id: city.name,
            label: `${city.name}, ${city.stateName}`
          }));

          const result = {
            items: cities,
            total: response.data.total_record,
          };

          cityCache.set(cacheKey, result);
          return result;
        }

        toast.error(response?.responseMessage);
        return { items: [], total: 0 };
      } catch (error) {
        console.error("Error fetching cities:", error);
        return { items: [], total: 0 };
      } finally {
        pendingCityRequests.delete(cacheKey);
      }
    })();

    pendingCityRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const fetchVehicle = useCallback(async (search, page, limit) => {
    try {
      const res = await getAllVehicleNumber({
        company_id: companyIdRef.current,
        offset: page,
        limit,
        search,
        firm_id: firmIdRef.current || ""
      });

      if (res?.responseCode === 200) {
        const list = res.data.vehicle_list.map((i) => ({
          ...i,
          label: i.vehicle_no,
          value: i._id
        }));

        return {
          items: list,
          total: res.data.total_record,
        };
      }

      toast.error(res?.responseMessage);
      return { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching vehicles:", error);
      return { items: [], total: 0 };
    }
  }, []);

  const fetchBranch = useCallback(async (search, page, limit) => {
    try {
      const response = await getBranchListApi({
        company_id: companyIdRef.current,
        offset: page,
        limit,
        search,
        firm_id: firmIdRef.current || ""
      });

      if (response?.responseCode === 200) {
        const branches = response.data.branch_list.map(branch => ({
          ...branch,
          label: branch.branch,
          value: branch._id
        }));

        return {
          items: branches,
          total: response.data.total_record || branches.length,
        };
      }

      toast.error(response?.responseMessage);
      return { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching branches:", error);
      return { items: [], total: 0 };
    }
  }, []);

  const fetchConsignor = useCallback(async (search, page, limit) => {
    try {
      const response = await getConsignorDropdownListData2({
        company_id: companyIdRef.current,
        firm_id: StorageService.getFirmId(),
        offset: page,
        limit,
        search,
        firm_id: firmIdRef.current || ""
      });

      if (response?.responseCode === 200) {
        const consignors = response.data.consignor_list.map(consignor => ({
          ...consignor,
          label: consignor.name,
          value: consignor._id
        }));

        return {
          items: consignors,
          total: response.data.total_record || consignors.length,
        };
      }

      toast.error(response?.responseMessage);
      return { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching consignors:", error);
      return { items: [], total: 0 };
    }
  }, []);

  const fetchSupplier = useCallback(async (search, page, limit) => {
    try {
      const response = await getAllSupplier({
        company_id: companyIdRef.current,
        offset: page,
        limit,
        search,
        firm_id: firmIdRef.current || ""
      });

      if (response?.responseCode === 200) {
        const suppliers = response.data.supplier_list.map(supplier => ({
          ...supplier,
          label: supplier.name,
          value: supplier._id
        }));

        return {
          items: suppliers,
          total: response.data.total_record || suppliers.length,
        };
      }

      toast.error(response?.responseMessage);
      return { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching suppliers:", error);
      return { items: [], total: 0 };
    }
  }, []);

  const fetchDriver = useCallback(async (search, page, limit) => {
    try {
      const response = await getAllDriver({
        company_id: companyIdRef.current,
        offset: page,
        limit,
        search,
        firm_id: firmIdRef.current || ""
      });

      if (response?.responseCode === 200) {
        const drivers = response.data.driver_list.map(driver => ({
          ...driver,
          label: driver.name,
          value: driver._id
        }));

        return {
          items: drivers,
          total: response.data.total_record || drivers.length,
        };
      }

      toast.error(response?.responseMessage);
      return { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching drivers:", error);
      return { items: [], total: 0 };
    }
  }, []);

  const fetchUser = useCallback(async (search, page, limit) => {
    try {
      const response = await getUser({
        company_id: companyIdRef.current,
        offset: page,
        limit,
        search,
        firm_id: firmIdRef.current || ""
      });

      if (response?.responseCode === 200) {
        const users = response.data.user_list.map(user => ({
          ...user,
          label: user.company_user_name,
          value: user?.user_id._id,
          _id: user?.user_id._id,
          name: user.company_user_name,
        }));

        return {
          items: users,
          total: response.data.total || users.length,
        };
      }

      toast.error(response?.responseMessage);
      return { items: [], total: 0 };
    } catch (error) {
      console.error("Error fetching users:", error);
      return { items: [], total: 0 };
    }
  }, []);

  const updateModalFilter = useCallback((updates) => {
    setModalFilter(prev => ({ ...prev, ...updates }));
  }, []);

  console.log('modal filter', modalFilter)

  // ✅ Fixed handleSave to save vehicle object properly
  const handleSave = useCallback(() => {
    const commonFilter = {
      search: modalFilter.search,
      branch_id: modalFilter.branch_id,
      branch_name: branchDetails?.branch || branchDetails?.label || "",
      start_date: modalFilter.start_date,
      end_date: modalFilter.end_date,
    };

    const filterConfigs = {
      plan: {
        additionalFields: [
          'consignor_id', 'plan_status', 'from_location', 'to_location',
          'bid_status', 'responses', 'vehicle_id', 'user_id'
        ],
        additionalData: {
          consignor_name: consignorDetails?.name || consignorDetails?.label || "",
          // ✅ Save vehicle object with all properties
          vehicle_no: vehicleNumber?._id ? {
            vehicle_no: vehicleNumber.vehicle_no,
            vehicle_ownership: vehicleNumber.vehicle_ownership
          } : null,
          user_name: userDetails?.name || userDetails?.label || "",
        },
        contextSetter: setPlanFilter,
        contextFilter: planFilter
      },
      Trip: {
        additionalFields: [
          'consignor_id', 'supplier_id', 'driver_id', 'from_location',
          'to_location', 'vehicle_ownership', 'trip_status', 'vehicle_id', 'offset'
        ],
        additionalData: {
          consignor_name: consignorDetails?.name || consignorDetails?.label || "",
          supplier_name: supplierDetails?.name || supplierDetails?.label || "",
          driver_name: driverDetails?.name || driverDetails?.label || "",
          // ✅ Save vehicle object with all properties
          vehicle_no: vehicleNumber?._id ? {
            vehicle_no: vehicleNumber.vehicle_no,
            vehicle_ownership: vehicleNumber.vehicle_ownership
          } : null,
          user_name: userDetails?.name || userDetails?.label || "",
        },
        contextSetter: setTRipFilter,
        contextFilter: filter
      },
      payment: {
        additionalFields: ['expense_category_id', 'user_id'],
        additionalData: {
          user_name: userDetails?.name || userDetails?.label || "",
        },
        customMapping: { param_user_id: modalFilter.user_id, serach: modalFilter.search }
      },
      Vehicle: {
        additionalFields: ['vehicle_ownership', 'vehicle_status', 'offset']
      },
      Location: {
        additionalFields: ['offset', 'stateCode']
      },
      PodFollowup: {
        additionalFields: [
          'consignor_id', 'supplier_id', 'driver_id', 'lr_status', 'vehicle_id', 'offset'
        ], // pod_status
        additionalData: {
          consignor_name: consignorDetails?.name || consignorDetails?.label || "",
          supplier_name: supplierDetails?.name || supplierDetails?.label || "",
          driver_name: driverDetails?.name || driverDetails?.label || "",
          vehicle_no: vehicleNumber?._id ? {
            vehicle_no: vehicleNumber.vehicle_no,
            vehicle_ownership: vehicleNumber.vehicle_ownership
          } : null,
        },
        contextSetter: setTRipFilter,
        contextFilter: filter
      },
    };

    const config = filterConfigs[name];
    if (!config) {
      console.warn("Unknown filter name:", name);
      return;
    }

    const additionalFilter = {};
    config.additionalFields?.forEach(field => {
      additionalFilter[field] = modalFilter[field];
    });

    const newFilter = {
      ...commonFilter,
      ...additionalFilter,
      ...config.additionalData,
      ...config.customMapping,
      offset: 1
    };

    setFilter(config.contextFilter ? { ...config.contextFilter, ...newFilter } : { ...filter, ...newFilter });

    if (config.contextSetter) {
      config.contextSetter({ ...config.contextFilter, ...newFilter });
    }

    onClose(false);
  }, [modalFilter, filter, name, setFilter, onClose, planFilter, setPlanFilter, setTRipFilter,
    branchDetails, consignorDetails, supplierDetails, driverDetails, vehicleNumber, userDetails]);

  // ✅ Fixed handleReset for both Trip and plan
  const handleReset = useCallback(() => {
    setFromLocationDetails({});
    setToLocationDetails({});
    setVehicleNumber({});
    setBranchDetails({});
    setConsignorDetails({});
    setSupplierDetails({});
    setDriverDetails({});
    setUserDetails({});
    setModalFilter(INITIAL_FILTER_STATE);
    setVehicleDetails({});

    prefilledCheckDone.current = false;

    const companyId = companyIdRef.current;
    const resetConfigs = {
      PodFollowup: () => setTRipFilter({
        company_id: companyId,
        firm_id: "",
        consignor_id: "",
        consignor_name: "",
        supplier_id: "",
        supplier_name: "",
        driver_id: "",
        driver_name: "",
        branch_id: "",
        branch_name: "",
        limit: 25,
        offset: 1,
        search: "",
        vehicle_id: "",
        vehicle_no: null, // ✅ Reset to null
        lr_status: "",
        // pod_status: ""
      })
    };

    resetConfigs[name]?.();
  }, [name, setTRipFilter, setPlanFilter, setSearchText, setVehicleDetails, setFilter]);

  const renderFormField = useCallback((type, props) => {
    const components = {
      search: () => (
        <div className="_inpW_search">
          <svg className="srcIcon" xmlns="http://www.w3.org/2000/svg" width="17" height="17" viewBox="0 0 17 17" fill="none">
            <path opacity="0.2" d="M16.5311 15.6665L11.9134 11.0488C13.023 9.71662 13.5764 8.00793 13.4583 6.27818C13.3402 4.54842 12.5598 2.93078 11.2794 1.76176C9.99906 0.592742 8.31727 -0.0376443 6.58394 0.0017403C4.8506 0.0411249 3.19918 0.747248 1.97322 1.97322C0.747248 3.19918 0.0411249 4.8506 0.0017403 6.58394C-0.0376443 8.31727 0.592742 9.99906 1.76176 11.2794C2.93078 12.5598 4.54842 13.3402 6.27818 13.4583C8.00793 13.5764 9.71662 13.023 11.0488 11.9134L15.6665 16.5311L16.5311 15.6665ZM1.24477 6.74783C1.24477 5.65943 1.56752 4.59547 2.1722 3.69049C2.77689 2.78552 3.63635 2.08018 4.6419 1.66366C5.64745 1.24715 6.75394 1.13817 7.82143 1.35051C8.88892 1.56285 9.86947 2.08696 10.6391 2.85658C11.4087 3.6262 11.9328 4.60675 12.1452 5.67424C12.3575 6.74173 12.2485 7.84821 11.832 8.85376C11.4155 9.85932 10.7101 10.7188 9.80517 11.3235C8.9002 11.9281 7.83623 12.2509 6.74783 12.2509C5.28883 12.2493 3.89004 11.669 2.85837 10.6373C1.82669 9.60562 1.24639 8.20684 1.24477 6.74783Z" fill="black" />
          </svg>
          <input
            type="text"
            placeholder="Search"
            className="simpleFilter_input"
            value={modalFilter.search}
            onChange={(e) => updateModalFilter({ search: e.target.value })}
          />
        </div>
      ),
      datePicker: () => (
        <CommonDatePicker
          name="date"
          label="Date"
          startDate={modalFilter.start_date}
          setStartDate={(data) => updateModalFilter({ start_date: data })}
        />
      ),
      dateRange: () => (
        <CommonDateRange
          initialStartDate={modalFilter.start_date || ""}
          initialEndDate={modalFilter.end_date || ""}
          onApply={(data) => updateModalFilter({
            start_date: moment(data.startDate),
            end_date: moment(data.endDate),
          })}
          datePrefield={!!(modalFilter.start_date && modalFilter.end_date)}
        />
      ),
      select: () => (
        <CustomSelect
          label={props.label}
          placeholder={props.placeholder}
          options={props.options}
          value={props.options?.find(item => item.value == props.selectedValue)}
          onSelect={(data) => updateModalFilter({ [props.field]: data?.value })}
        />
      ),
      cityDropdown: () => (
        <CityDropdownForTrip
          label={props.label}
          placeholder="Select Location"
          fetchData={fetchFromCity}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?.name });
            props.setLocationDetails(data);
          }}
          selectedItem={props.selectedItem}
        />
      ),
      vehicleDropdown: () => (
        <VehicleDropdownForTrip
          label={props.label}
          placeholder="All"
          fetchData={fetchVehicle}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?._id });
            props.setVehicleNumber(data);
            setVehicleDetails(data);
          }}
          selectedItem={props.selectedItem} // vehicleDeatils // ✅ use local state passed via props
        />
      ),
      branchDropdown: () => (
        <CityDropdownForTrip
          label={props.label}
          placeholder="Select Branch"
          fetchData={fetchBranch}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?._id });
            props.setBranchDetails(data);
          }}
          selectedItem={props.selectedItem}
        />
      ),
      consignorDropdown: () => (
        <CityDropdownForTrip
          label={props.label}
          placeholder="Select Consignor"
          fetchData={fetchConsignor}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?._id });
            props.setConsignorDetails(data);
          }}
          selectedItem={props.selectedItem}
        />
      ),
      supplierDropdown: () => (
        <CityDropdownForTrip
          label={props.label}
          placeholder="Select Supplier"
          fetchData={fetchSupplier}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?._id });
            props.setSupplierDetails(data);
          }}
          selectedItem={props.selectedItem}
        />
      ),
      driverDropdown: () => (
        <CityDropdownForTrip
          label={props.label}
          placeholder="Select Driver"
          fetchData={fetchDriver}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?._id });
            props.setDriverDetails(data);
          }}
          selectedItem={props.selectedItem}
        />
      ),
      userDropdown: () => (
        <CityDropdownForTrip
          label={props.label}
          placeholder="Select User"
          fetchData={fetchUser}
          pageSize={20}
          onChange={(data) => {
            updateModalFilter({ [props.field]: data?._id });
            props.setUserDetails(data);
          }}
          selectedItem={props.selectedItem}
        />
      )
    };

    return components[type]?.();
  }, [modalFilter, updateModalFilter, fetchFromCity, fetchVehicle, fetchBranch, fetchConsignor,
    fetchSupplier, fetchDriver, fetchUser, setVehicleDetails, vehicleDeatils]);

  if (!isOpen) return null;

  return (
    <>
      <div className="offcanvas-header">
        <div className="d-flex gap-2 align-items-center">
          <h5 className="offcanvas-title">Filters</h5>
          {filterCount > 0 ? <div className="_counts">{filterCount}</div> : <></>}
        </div>
        <CrossButton
          type="button"
          className="_switchBtn __animEffect_btn"
          style={{ marginLeft: 'auto' }}
          onClick={() => onClose(false)}
        >
          <svg className="cross" xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 13 13" fill="none">
            <path d="M12.5321 0.477671C12.4422 0.387555 12.3353 0.316061 12.2177 0.267281C12.1001 0.2185 11.9741 0.193391 11.8468 0.193391C11.7195 0.193391 11.5934 0.2185 11.4758 0.267281C11.3582 0.316061 11.2514 0.387555 11.1615 0.477671L6.40798 5.22143L1.6545 0.46795C1.56451 0.377952 1.45766 0.306563 1.34008 0.257857C1.22249 0.20915 1.09646 0.184082 0.969185 0.184082C0.84191 0.184082 0.715881 0.20915 0.598294 0.257857C0.480707 0.306563 0.373865 0.377952 0.283868 0.46795C0.19387 0.557947 0.122481 0.664789 0.0737746 0.782376C0.0250685 0.899963 -9.48274e-10 1.02599 0 1.15327C9.48275e-10 1.28054 0.0250684 1.40657 0.0737746 1.52416C0.122481 1.64175 0.19387 1.74859 0.283868 1.83858L5.03735 6.59206L0.283868 11.3455C0.19387 11.4355 0.122481 11.5424 0.0737746 11.66C0.0250685 11.7776 0 11.9036 0 12.0309C0 12.1581 0.0250685 12.2842 0.0737746 12.4017C0.122481 12.5193 0.19387 12.6262 0.283868 12.7162C0.373865 12.8062 0.480707 12.8776 0.598294 12.9263C0.715881 12.975 0.84191 13 0.969185 13C1.09646 13 1.22249 12.975 1.34008 12.9263C1.45766 12.8776 1.56451 12.8062 1.6545 12.7162L6.40798 7.9627L11.1615 12.7162C11.2515 12.8062 11.3583 12.8776 11.4759 12.9263C11.5935 12.975 11.7195 13 11.8468 13C11.974 13 12.1001 12.975 12.2177 12.9263C12.3353 12.8776 12.4421 12.8062 12.5321 12.7162C12.6221 12.6262 12.6935 12.5193 12.7422 12.4017C12.7909 12.2842 12.816 12.1581 12.816 12.0309C12.816 11.9036 12.7909 11.7776 12.7422 11.66C12.6935 11.5424 12.6221 11.4355 12.5321 11.3455L7.77861 6.59206L12.5321 1.83858C12.9015 1.46919 12.9015 0.847062 12.5321 0.477671Z" fill="black" />
          </svg>
        </CrossButton>
      </div>

      <div className="offcanvas-body">
        <div className="_tableFilter_inrFx">
          <div className="_leftFilts">
            {search && <div className="_filtrFlx">
              {renderFormField('search')}
            </div>}

            {/* {isShow?.datePicker && (
              <div className="singleDiv">
                {renderFormField('datePicker')}
              </div>
            )} */}

            {/* {isShow?.dateRange && (
              <div className="singleDiv">
                {renderFormField('dateRange')}
              </div>
            )} */}

            {isShow?.allBranch && (
              <div className="singleDiv">
                {renderFormField('branchDropdown', {
                  label: "Branch",
                  field: 'branch_id',
                  selectedItem: branchDetails,
                  setBranchDetails: setBranchDetails
                })}
              </div>
            )}

            {/* {isShow?.allUser && (
              <div className="singleDiv">
                {renderFormField('userDropdown', {
                  label: "User",
                  field: 'user_id',
                  selectedItem: userDetails,
                  setUserDetails: setUserDetails
                })}
              </div>
            )} */}

            {/* {isShow?.expenseCatagory && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "Expenses Category",
                  placeholder: "All Categories",
                  options: list?.expense,
                  selectedValue: modalFilter.expense_category_id,
                  field: 'expense_category_id'
                })}
              </div>
            )} */}

            {isShow?.consignor && (
              <div className="singleDiv">
                {renderFormField('consignorDropdown', {
                  label: "Consignor",
                  field: 'consignor_id',
                  selectedItem: consignorDetails,
                  setConsignorDetails: setConsignorDetails
                })}
              </div>
            )}

            {isShow?.supplier && (
              <div className="singleDiv">
                {renderFormField('supplierDropdown', {
                  label: "Supplier",
                  field: 'supplier_id',
                  selectedItem: supplierDetails,
                  setSupplierDetails: setSupplierDetails
                })}
              </div>
            )}

            {isShow?.driver && (
              <div className="singleDiv">
                {renderFormField('driverDropdown', {
                  label: "Driver",
                  field: 'driver_id',
                  selectedItem: driverDetails,
                  setDriverDetails: setDriverDetails
                })}
              </div>
            )}

            {isShow?.fromLocation && (
              <div className="singleDiv">
                {renderFormField('cityDropdown', {
                  label: "From Location",
                  field: 'from_location',
                  selectedItem: fromLocationDetails,
                  setLocationDetails: setFromLocationDetails
                })}
              </div>
            )}

            {isShow?.toLocation && (
              <div className="singleDiv">
                {renderFormField('cityDropdown', {
                  label: "To Location",
                  field: 'to_location',
                  selectedItem: toLocationDetails,
                  setLocationDetails: setToLocationDetails
                })}
              </div>
            )}

            {isShow?.lr_status && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "LR Status",
                  placeholder: "LR Status",
                  options: dropdownLists.lrStatusList || [],
                  selectedValue: modalFilter.lr_status,
                  field: 'lr_status'
                })}
              </div>
            )}

            {/* {isShow?.pod_status && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "POD Status",
                  placeholder: "POD Status",
                  options: dropdownLists.podStatusList || [],
                  selectedValue: modalFilter.pod_status,
                  field: 'pod_status'
                })}
              </div>
            )} */}

            {isShow?.vehicle && (
              <div className="singleDiv">
                {renderFormField('vehicleDropdown', {
                  label: "Vehicle",
                  field: 'vehicle_id',
                  selectedItem: vehicleNumber,
                  setVehicleNumber: setVehicleNumber
                })}
              </div>
            )}

            {/* {isShow?.tripStatus && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "Status",
                  placeholder: "Select Status",
                  options: dropdownLists.tripStatusList || [],
                  selectedValue: modalFilter.trip_status,
                  field: 'trip_status'
                })}
              </div>
            )} */}

            {/* {isShow?.planStatus && (
              <div className="singleDiv">
                <CustomSelect
                  label="Plan Status"
                  placeholder="Select Status"
                  options={dropdownLists.planStatusList || []}
                  value={dropdownLists.planStatusList?.find(i => i?.value == modalFilter?.plan_status)}
                  onSelect={(data) => updateModalFilter({
                    plan_status: data?.value,
                    bid_status: data?.value == 1 ? 0 : modalFilter.bid_status,
                    responses: data?.value == 1 ? 0 : modalFilter.responses,
                  })}
                />
              </div>
            )} */}

            {/* {isShow?.bidStatus && modalFilter?.plan_status == 1 && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "Bid Status",
                  placeholder: "Select Status",
                  options: dropdownLists.bidStatusList || [],
                  selectedValue: modalFilter.bid_status,
                  field: 'bid_status'
                })}
              </div>
            )} */}

            {/* {isShow?.responses && modalFilter?.plan_status == 1 && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "Responses",
                  placeholder: "Select Status",
                  options: dropdownLists.responseList || [],
                  selectedValue: modalFilter.responses,
                  field: 'responses'
                })}
              </div>
            )} */}

            {/* {isShow?.vehicle_status && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "Status",
                  placeholder: "Select Status",
                  options: dropdownLists.vehicleStatusList || [],
                  selectedValue: modalFilter.vehicle_status,
                  field: 'vehicle_status'
                })}
              </div>
            )} */}

            {/* {isShow?.state && (
              <div className="singleDiv">
                {renderFormField('select', {
                  label: "State",
                  placeholder: "Select States",
                  options: dropdownLists.stateList || [],
                  selectedValue: modalFilter.stateCode,
                  field: 'stateCode'
                })}
              </div>
            )} */}
          </div>
        </div>
      </div>

      <div className="offcanvas-footer">
        <AddButton
          name="Save"
          className="_addComm_btn"
          onClick={handleSave}
          buttonTitle="Save"
        />
        <button className="_cancel_btn" onClick={handleReset}>
          Reset
        </button>
      </div>
    </>
  );
};

export default PodFollowupFilterModal;
